/**
 * Hierarchical output preview — shows assembled XML/JSON with a readable raw
 * view (pretty-printed) and a collapsible tree view.
 *
 * The server returns the serialized bytes as-is (XML is typically one long
 * line with no indentation), so we pretty-print on the client for display.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  AccountTree as TreeIcon,
  Code as CodeIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { previewAssemblyOutput } from '../../services/workspaceApi';

interface HierarchicalPreviewProps {
  sessionId: string;
  outputRef: string;
  outputFormat?: string;
  byteSize?: number;
  onDownload?: () => void;
  /**
   * Plan §"Annotated preview" — when ReadinessFindings are supplied,
   * the preview renders an inline banner near the top listing the
   * offending element paths so the user can correlate the To-fix list
   * with the output. Full XPath-walking inline-badge overlay is the
   * deeper restructure; this banner is the first delivery of the
   * spatial correlation the plan promised.
   */
  findings?: Array<{
    code: string;
    severity: string;
    target_path: string;
    target_element: string;
    user_title: string;
  }>;
  /**
   * When this value changes, the preview re-fetches its content from
   * storage. Used by the parent to surface a manual "refresh preview"
   * action after the underlying assembly bytes have been recomputed
   * (e.g. on pipeline rerun — same step_id keeps the same outputRef but
   * the bytes at that path are overwritten).
   */
  refreshKey?: number;
}

/* ── Pretty-printers ────────────────────────────────────────────────── */

/** Indent an XML string.  Handles text nodes, self-closing tags, declarations,
 *  and comments.  Input is expected to be well-formed-ish; on any parse failure
 *  falls back to the original string. */
function prettyPrintXml(xml: string): string {
  if (!xml) return '';
  try {
    const tokens = xml.match(/<!--[\s\S]*?-->|<\?[\s\S]*?\?>|<\/?[^>]+>|[^<]+/g) || [];
    const out: string[] = [];
    let depth = 0;
    const pad = (d: number) => '  '.repeat(Math.max(0, d));

    for (const raw of tokens) {
      const tok = raw.trim();
      if (!tok) continue;

      if (tok.startsWith('<!--') || tok.startsWith('<?')) {
        out.push(pad(depth) + tok);
      } else if (tok.startsWith('</')) {
        depth = Math.max(0, depth - 1);
        out.push(pad(depth) + tok);
      } else if (tok.startsWith('<') && tok.endsWith('/>')) {
        out.push(pad(depth) + tok);
      } else if (tok.startsWith('<')) {
        out.push(pad(depth) + tok);
        depth += 1;
      } else {
        // Text node — append to previous line to keep <tag>text</tag> on one line
        if (out.length > 0) {
          out[out.length - 1] = out[out.length - 1] + tok;
        } else {
          out.push(pad(depth) + tok);
        }
      }
    }
    return out.join('\n');
  } catch {
    return xml;
  }
}

function prettyPrintJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/* ── Tree view (XML) ────────────────────────────────────────────────── */

interface TreeNode {
  name: string;
  attributes: Array<{ name: string; value: string }>;
  text?: string; // non-empty when the element has text content only
  children: TreeNode[];
}

/** Parse XML into a lightweight tree.  Returns null on parse failure. */
function parseXmlTree(xml: string): TreeNode | null {
  if (!xml) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    const err = doc.querySelector('parsererror');
    if (err) return null;
    const root = doc.documentElement;
    if (!root) return null;
    return domToTree(root);
  } catch {
    return null;
  }
}

function domToTree(el: Element): TreeNode {
  const attributes = Array.from(el.attributes).map((a) => ({ name: a.name, value: a.value }));
  const childElements = Array.from(el.children);
  const node: TreeNode = {
    name: el.nodeName,
    attributes,
    children: childElements.map(domToTree),
  };
  if (childElements.length === 0) {
    const text = el.textContent?.trim() || '';
    if (text) node.text = text;
  }
  return node;
}

const TreeElement: React.FC<{ node: TreeNode; defaultOpen?: boolean; indent?: number }> = ({
  node,
  defaultOpen = true,
  indent = 0,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = node.children.length > 0;
  const isLeafText = !hasChildren && !!node.text;

  return (
    <Box sx={{ pl: `${indent * 12}px`, fontFamily: '"Fira Code", monospace', fontSize: '0.75rem' }}>
      <Box
        onClick={() => hasChildren && setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          py: '1px',
          cursor: hasChildren ? 'pointer' : 'default',
          '&:hover': hasChildren ? { bgcolor: 'action.hover' } : {},
        }}
      >
        {hasChildren ? (
          open ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ChevronRightIcon sx={{ fontSize: 14 }} />
        ) : (
          <Box sx={{ width: 14 }} />
        )}
        <Box component="span" sx={{ color: '#1976d2' }}>&lt;{node.name}</Box>
        {node.attributes.map((a) => (
          <React.Fragment key={a.name}>
            {' '}
            <Box component="span" sx={{ color: '#7b1fa2' }}>{a.name}</Box>
            <Box component="span">=</Box>
            <Box component="span" sx={{ color: '#388e3c' }}>"{a.value}"</Box>
          </React.Fragment>
        ))}
        <Box component="span" sx={{ color: '#1976d2' }}>
          {isLeafText ? '>' : hasChildren ? '>' : '/>'}
        </Box>
        {isLeafText && (
          <>
            <Box component="span" sx={{ color: '#424242', ml: 0.5 }}>{node.text}</Box>
            <Box component="span" sx={{ color: '#1976d2' }}>&lt;/{node.name}&gt;</Box>
          </>
        )}
        {hasChildren && !open && (
          <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontStyle: 'italic' }}>
            … ({node.children.length} child{node.children.length === 1 ? '' : 'ren'})
          </Box>
        )}
      </Box>

      {hasChildren && open && (
        <>
          {node.children.map((c, i) => (
            <TreeElement key={i} node={c} defaultOpen={indent < 2} indent={indent + 1} />
          ))}
          <Box sx={{ pl: 0, color: '#1976d2' }}>&lt;/{node.name}&gt;</Box>
        </>
      )}
    </Box>
  );
};

const JsonTreeView: React.FC<{ value: any; name?: string; indent?: number; defaultOpen?: boolean }> = ({
  value,
  name,
  indent = 0,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  const keys = isObject ? Object.keys(value) : [];

  if (!isObject) {
    const color = typeof value === 'string' ? '#388e3c' : typeof value === 'number' ? '#1565c0' : '#c62828';
    const display = typeof value === 'string' ? `"${value}"` : String(value);
    return (
      <Box sx={{ pl: `${indent * 12}px`, py: '1px', fontFamily: '"Fira Code", monospace', fontSize: '0.75rem' }}>
        {name != null && <Box component="span" sx={{ color: '#7b1fa2' }}>{name}: </Box>}
        <Box component="span" sx={{ color }}>{display}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pl: `${indent * 12}px`, fontFamily: '"Fira Code", monospace', fontSize: '0.75rem' }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer', py: '1px', display: 'flex', alignItems: 'center', '&:hover': { bgcolor: 'action.hover' } }}
      >
        {open ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ChevronRightIcon sx={{ fontSize: 14 }} />}
        {name != null && <Box component="span" sx={{ color: '#7b1fa2' }}>{name}: </Box>}
        <Box component="span">{isArray ? '[' : '{'}</Box>
        {!open && (
          <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontStyle: 'italic' }}>
            … {keys.length} {isArray ? 'items' : 'keys'}
          </Box>
        )}
        {!open && <Box component="span">{isArray ? ']' : '}'}</Box>}
      </Box>
      {open && (
        <>
          {keys.map((k) => (
            <JsonTreeView
              key={k}
              value={(value as any)[k]}
              name={isArray ? undefined : k}
              indent={indent + 1}
              defaultOpen={indent < 1}
            />
          ))}
          <Box>{isArray ? ']' : '}'}</Box>
        </>
      )}
    </Box>
  );
};

/* ── Main component ─────────────────────────────────────────────────── */

const HierarchicalPreview: React.FC<HierarchicalPreviewProps> = ({
  sessionId,
  outputRef,
  outputFormat = 'xml',
  byteSize,
  onDownload,
  findings,
  refreshKey,
}) => {
  const [viewMode, setViewMode] = useState<'raw' | 'tree'>('tree');
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!outputRef) return;
    setLoading(true);
    setError(null);

    const fetchPreview = async () => {
      try {
        // Fetch up to 2 MB so typical ODM outputs (often 100–500 KB) arrive
        // complete and the tree view can parse the whole document.  Larger
        // files still stream via the Download button.
        const data = await previewAssemblyOutput(sessionId, outputRef, 2_000_000);
        setContent(data.content || '');
        setTruncated(data.truncated || false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [sessionId, outputRef, refreshKey]);

  const isJson = outputFormat.toLowerCase() === 'json';

  // Truncated XML/JSON often isn't parseable at the tail.  Strip trailing
  // partial token before attempting formatting / parsing.
  const cleanContent = useMemo(() => {
    if (!content) return '';
    if (!truncated) return content;
    if (isJson) return content; // JSON tree parses best-effort below
    // Drop the last partial tag / text fragment at the cut-off point.
    const lastClose = content.lastIndexOf('>');
    return lastClose > 0 ? content.slice(0, lastClose + 1) : content;
  }, [content, truncated, isJson]);

  const prettyContent = useMemo(() => {
    if (!cleanContent) return '';
    return isJson ? prettyPrintJson(cleanContent) : prettyPrintXml(cleanContent);
  }, [cleanContent, isJson]);

  const xmlTree = useMemo(() => {
    if (isJson) return null;
    // Try as-is first.  For truncated XML, walk the token stream, track the
    // open-tag stack, and append closers so DOMParser can parse a best-effort
    // tree rather than failing wholesale.
    const direct = parseXmlTree(cleanContent);
    if (direct) return direct;
    if (!truncated) return null;
    const tokens = cleanContent.match(/<\/?[^>]+>/g) || [];
    const stack: string[] = [];
    for (const t of tokens) {
      if (t.startsWith('<?') || t.startsWith('<!--')) continue;
      if (t.startsWith('</')) {
        const name = t.slice(2, -1).split(/\s/)[0];
        while (stack.length && stack[stack.length - 1] !== name) stack.pop();
        if (stack.length) stack.pop();
      } else if (!t.endsWith('/>')) {
        const name = t.slice(1, -1).split(/\s/)[0];
        if (name && !name.startsWith('!')) stack.push(name);
      }
    }
    const patched = cleanContent + stack.reverse().map((n) => `</${n}>`).join('');
    return parseXmlTree(patched);
  }, [cleanContent, isJson, truncated]);
  const jsonTree = useMemo(() => {
    if (!isJson) return null;
    try { return JSON.parse(cleanContent); } catch { return null; }
  }, [cleanContent, isJson]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={outputFormat.toUpperCase()} size="small" variant="outlined" color="primary" />
          {byteSize != null && (
            <Typography variant="caption" color="text.secondary">
              {(byteSize / 1024).toFixed(1)} KB
            </Typography>
          )}
          {truncated && (
            <Chip label="Truncated" size="small" color="warning" variant="outlined" />
          )}
          {/* When the host passes Readiness findings, surface a small
              "preview only" chip + count so the user knows this is not
              a submittable artifact. Plan §"Preview" copy: "Download
              preview (not for submission)" framing. */}
          {findings && findings.some((f) => f.severity === 'blocker') && (
            <Chip
              label="Preview — not for submission"
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)} sx={{ minHeight: 32 }}>
            <Tab value="tree" label="Tree" icon={<TreeIcon sx={{ fontSize: 14 }} />} iconPosition="start" sx={{ minHeight: 32, py: 0 }} />
            <Tab value="raw" label="Raw" icon={<CodeIcon sx={{ fontSize: 14 }} />} iconPosition="start" sx={{ minHeight: 32, py: 0 }} />
          </Tabs>
          {onDownload && (
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={onDownload}>
              Download {outputFormat.toUpperCase()}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Plan §"Annotated preview" — when findings are present, list
          the offending element paths at the top of the preview so the
          user can correlate the To-fix list with the rendered output.
          A full XPath-walking inline-badge overlay is the deeper
          restructure (would require deep edits to the recursive
          TreeElement renderer); this banner closes the spatial gap
          today by naming the element + plain-English title for every
          blocker finding. */}
      {findings && findings.length > 0 && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.light',
            bgcolor: 'rgba(255, 167, 38, 0.05)',
          }}
        >
          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
            Issues in this output ({findings.length})
          </Typography>
          <Stack spacing={0.5}>
            {findings.slice(0, 6).map((f, idx) => (
              <Typography
                key={`${f.code}-${f.target_path}-${idx}`}
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontSize: '0.7rem' }}
              >
                {f.severity === 'blocker' ? '⛔' : '⚠'}{' '}
                <Box component="span" sx={{ fontFamily: '"Fira Code", monospace' }}>
                  {f.target_element}
                </Box>{' '}
                — {f.user_title}
              </Typography>
            ))}
            {findings.length > 6 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                …and {findings.length - 6} more
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {viewMode === 'raw' && (
        <Box
          component="pre"
          sx={{
            maxHeight: 400,
            overflow: 'auto',
            bgcolor: '#1e1e1e',
            color: '#d4d4d4',
            p: 1.5,
            m: 0,
            borderRadius: 1,
            fontFamily: '"Fira Code", monospace',
            fontSize: '0.75rem',
            whiteSpace: 'pre',
            tabSize: 2,
          }}
        >
          {prettyContent || '(empty)'}
        </Box>
      )}

      {viewMode === 'tree' && (
        <Box sx={{ maxHeight: 400, overflow: 'auto', p: 1, bgcolor: '#fafafa', borderRadius: 1 }}>
          {isJson ? (
            jsonTree !== null ? (
              <JsonTreeView value={jsonTree} defaultOpen />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Unable to parse JSON tree{truncated ? ' (output is truncated — download for full content)' : ''}.
              </Typography>
            )
          ) : (
            xmlTree ? (
              <TreeElement node={xmlTree} defaultOpen />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Unable to parse XML tree{truncated ? ' (output is truncated — download for full content)' : ''}.
              </Typography>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

export default HierarchicalPreview;
